// SEO-metadata per route, baserat på SEO-arbetet från tiego.se.
// Nyckel = path UTAN språkprefix (t.ex. "/tga", "/", "/kommuner").
// Komponenten <SEO /> i src/components/SEO.tsx matchar automatiskt nuvarande språk.

export interface SeoLocalized {
  title: string;
  description: string;
  keywords?: string[];
}

export interface SeoEntry {
  sv: SeoLocalized;
  en: SeoLocalized;
}

export const seoMeta: Record<string, SeoEntry> = {
  "/": {
    sv: {
      title: "Tiego | Programvara för effektiv koncernkonsolidering",
      description:
        "Förenkla koncernrapportering och leasinghantering med Tiego. Vårt system uppfyller kraven K3, IFRS 16 och RKR R5.",
      keywords: ["tiego", "koncernkonsolidering", "koncernredovisning", "leasingredovisning"],
    },
    en: {
      title: "Tiego | Software for efficient group consolidation",
      description:
        "Simplify group reporting and lease management with Tiego. Our system meets K3, IFRS 16 and RKR R5 requirements.",
      keywords: ["tiego", "group consolidation", "group accounting", "lease accounting"],
    },
  },
  "/tga": {
    sv: {
      title: "TGA: Effektivt koncernrapporteringssystem",
      description:
        "Tiego Group Accounting förenklar koncernrapportering med månatliga balansräkningar, valutakonvertering och automatisk kassaflödesanalys.",
      keywords: ["koncernrapporteringssystem", "koncernrapportering", "TGA"],
    },
    en: {
      title: "Streamlined Group Reporting & Accounting Software",
      description:
        "Tiego Group Accounting simplifies group reporting with monthly balances, currency conversion, and automated cash flow analysis.",
      keywords: ["group accounting software", "group reporting software", "TGA"],
    },
  },
  "/tga/kundberattelser": {
    sv: {
      title: "TGA Kundberättelser – Eidra, Stockwik, Indutrade m.fl.",
      description:
        "Hör hur ekonomichefer på Eidra, Stockwik och Indutrade använder Tiego Group Accounting för smidigare koncernredovisning och rapportering.",
    },
    en: {
      title: "TGA Customer Stories – Eidra, Stockwik, Indutrade",
      description:
        "Hear how CFOs at Eidra, Stockwik and Indutrade use Tiego Group Accounting for smoother group reporting and consolidation.",
    },
  },
  "/tle": {
    sv: {
      title: "TLE: Leasingredovisning för IFRS 16, K3 & RKR",
      description:
        "Tiego Lease Engine effektiviserar leasinghantering för IFRS 16, K3 och RKR R5 med stöd för finansiell och operationell leasing.",
      keywords: ["leasingredovisning", "IFRS 16", "K3", "RKR R5", "leasingsystem"],
    },
    en: {
      title: "TLE: Lease Accounting Software for IFRS 16, K3, RKR",
      description:
        "Tiego Lease Engine streamlines lease management for IFRS 16, K3 and RKR R5 with support for financial and operational leases.",
      keywords: ["lease accounting software", "lease accounting system", "IFRS 16", "K3", "RKR"],
    },
  },
  "/tle/kundberattelser": {
    sv: {
      title: "TLE Kundberättelser – kommuner och bolag",
      description:
        "Se hur kommuner och företag använder Tiego Lease Engine för att hantera leasingavtal enligt IFRS 16, K3 och RKR R5.",
    },
    en: {
      title: "TLE Customer Stories – municipalities and companies",
      description:
        "See how municipalities and companies use Tiego Lease Engine to manage lease contracts under IFRS 16, K3 and RKR R5.",
    },
  },
  "/tle-implementation": {
    sv: {
      title: "Kostnadsfri workshop och demo av Tiego Lease Engine",
      description:
        "Boka en gratis demo och workshop med Tiego för att implementera IFRS 16, K3 eller RKR R5. Se hur Tiego Lease Engine effektiviserar leasingrapportering.",
    },
    en: {
      title: "Free workshop and demo of Tiego Lease Engine",
      description:
        "Book a free demo and workshop with Tiego to implement IFRS 16, K3, or RKR R5. See how Tiego Lease Engine streamlines lease reporting.",
      keywords: ["lease accounting implementation"],
    },
  },
  "/integrationer": {
    sv: {
      title: "Tiego Integration Framework",
      description:
        "Tiego Integration Framework ger kontroll och flexibilitet för rapportering, oavsett system. Tillsammans skapar vi skräddarsydda integrationslösningar.",
    },
    en: {
      title: "Tiego Integration Framework",
      description:
        "Tiego Integration Framework offers control and flexibility for reporting, regardless of your system. Together we create tailored integration solutions.",
    },
  },
  "/implementation": {
    sv: {
      title: "Kostnadsfri workshop och demo för Koncernrapportering",
      description:
        "Tiego erbjuder en kostnadsfri workshop för att implementera koncernrapportering. Se hur vi hanterar transaktionsimport, avstämning och kassaflödesanalyser.",
    },
    en: {
      title: "Free workshop and demo of Tiego Group Accounting Software",
      description:
        "Tiego offers a free workshop to implement group reporting. See how Tiego handles transaction imports, reconciliation and cash flow analysis.",
      keywords: ["group accounting software implementation"],
    },
  },
  "/kommuner": {
    sv: {
      title: "Leasingredovisning för kommuner enligt RKR R5",
      description:
        "K3, IFRS 16 och RKR R5 för leasingredovisning i kommuner. Lär dig hantera finansiella leasingavtal och säkra en korrekt balansräkning med rätt systemstöd.",
      keywords: ["leasingredovisning", "RKR R5", "system för finansiell leasing", "leasingsystem"],
    },
    en: {
      title: "RKR R5 Municipal Lease Accounting Software",
      description:
        "K3, IFRS 16, and RKR R5 for lease accounting in municipalities. Learn how to manage financial lease agreements with the right system support.",
      keywords: ["municipal lease accounting software", "RKR R5"],
    },
  },
  "/export-api": {
    sv: {
      title: "Export API för koncernrapportering",
      description:
        "Exportera koncerndata från Tiego till BI-verktyg, datalager och rapporteringsplattformar via Tiegos Export API.",
    },
    en: {
      title: "Export API for group reporting",
      description:
        "Export group data from Tiego to BI tools, data warehouses and reporting platforms via Tiego's Export API.",
    },
  },
  "/nyheter": {
    sv: {
      title: "Företagsnyheter och utvecklingar inom vår verksamhet",
      description:
        "Håll dig uppdaterad med de senaste företagsnyheterna och utvecklingarna. Utforska hur vi förbättrar och utvecklar våra tjänster.",
    },
    en: {
      title: "News and developments in our operations",
      description:
        "Stay updated with the latest company news and developments. Explore how we improve and evolve our services.",
    },
  },
  "/filmer": {
    sv: {
      title: "Filmer om koncernredovisning, leasing och revision",
      description:
        "Se Tiegos expertfilmer om koncernredovisning, leasing enligt IFRS 16/K3/RKR R5, outsourcing och revision – praktiska tips från våra specialister.",
    },
    en: {
      title: "Videos on group accounting, leasing and auditing",
      description:
        "Watch Tiego's expert videos on group accounting, leasing under IFRS 16/K3/RKR R5, outsourcing and auditing – practical tips from our specialists.",
    },
  },
  "/karriar": {
    sv: {
      title: "Vi söker koncernredovisningskonsulter – Ansök nu!",
      description:
        "Tiego söker konsulter med redovisningsbakgrund för koncernredovisning. Ansök nu och bli en del av vårt team.",
    },
    en: {
      title: "Consolidation Consultant Jobs – Apply Now!",
      description:
        "Tiego is looking for consultants with accounting experience to work on consolidation solutions. Apply today!",
    },
  },
  "/medarbetare": {
    sv: {
      title: "Möt vårt team – Tiegos medarbetare",
      description:
        "Lär känna teamet bakom Tiego – erfarna specialister inom koncernredovisning, leasing och systemutveckling.",
    },
    en: {
      title: "Meet our team – Tiego employees",
      description:
        "Get to know the team behind Tiego – experienced specialists in group accounting, leasing and software development.",
    },
  },
  "/support": {
    sv: {
      title: "Support – Tiego Group Accounting & Lease Engine",
      description:
        "Behöver du hjälp med Tiego Group Accounting eller Lease Engine? Kontakta vår support – vi hjälper dig snabbt vidare.",
    },
    en: {
      title: "Support – Tiego Group Accounting & Lease Engine",
      description:
        "Need help with Tiego Group Accounting or Lease Engine? Contact our support team – we'll get you back on track quickly.",
    },
  },
  "/book-a-demo": {
    sv: {
      title: "Boka demo av Tiegos koncernredovisningssystem",
      description:
        "Upptäck hur Tiego kan revolutionera er koncernredovisning och leasing. Boka en demo för att se våra systemlösningar.",
    },
    en: {
      title: "Book a Demo: Solutions for Consolidated Reporting",
      description:
        "Discover how Tiego can revolutionize your consolidated reporting and leasing processes. Book a demo to see our system solutions in action.",
      keywords: ["group accounting software demo"],
    },
  },
  "/ai-policy": {
    sv: {
      title: "Tiegos AI-policy: så använder vi AI och skyddar dina data",
      description:
        "Tiegos AI-policy beskriver hur vi använder AI internt och i våra produkter, samt hur vi värnar om dataskydd och transparens.",
    },
    en: {
      title: "Tiego's AI Policy: How we use AI and protect your data",
      description:
        "Tiego's AI policy describes how we use AI internally and in our products, and how we protect data and ensure transparency.",
    },
  },

  // TW-62: förslag till nya titlar/beskrivningar för utkastsidorna (noindex). Flyttas till
  // "/", "/tga" och "/tle" när utkasten ersätter dagens sidor.
  "/utkast": {
    sv: {
      title: "Tiego | Koncernredovisning och leasingredovisning",
      description:
        "Tiego utvecklar TGA för koncernredovisning och TLE för leasingredovisning enligt K3, IFRS och RKR R5, med konsulter som kan redovisningen bakom systemen.",
      keywords: ["tiego", "koncernredovisning", "leasingredovisning", "koncernkonsolidering"],
    },
    en: {
      title: "Tiego | Group accounting and lease accounting software",
      description:
        "Tiego develops TGA for group accounting and TLE for lease accounting under K3, IFRS and RKR R5, backed by consultants who know the accounting.",
      keywords: ["tiego", "group accounting", "lease accounting", "group consolidation"],
    },
  },
  "/utkast/tga": {
    sv: {
      title: "TGA: System för koncernredovisning enligt K3 och IFRS",
      description:
        "TGA är byggt av koncernredovisare för koncerner som förändras: förvärv rätt från första bokslutet, spårbara elimineringar och låsta siffror med historik.",
      keywords: ["koncernredovisning", "koncernredovisningssystem", "konsolidering", "IFRS", "K3", "TGA"],
    },
    en: {
      title: "TGA: Group accounting software for K3 and IFRS",
      description:
        "TGA is built by group accountants for groups that change: acquisitions right from the first close, traceable eliminations and locked figures with history.",
      keywords: ["group accounting", "consolidation software", "IFRS", "K3", "TGA"],
    },
  },
  "/utkast/tle": {
    sv: {
      title: "TLE: Leasingredovisning för IFRS 16, K3 och RKR R5",
      description:
        "TLE räknar över 30 000 leasingkontrakt varje månad enligt IFRS 16, K3 och RKR R5, med full retroaktivitet, audit trail och inläsning av avtal med AI.",
      keywords: ["leasingredovisning", "IFRS 16", "K3", "RKR R5", "leasingsystem", "TLE"],
    },
    en: {
      title: "TLE: Lease accounting software for IFRS 16, K3, RKR R5",
      description:
        "TLE calculates over 30,000 lease contracts every month under IFRS 16, K3 and RKR R5, with full retroactivity, an audit trail and AI contract intake.",
      keywords: ["lease accounting", "IFRS 16", "K3", "RKR R5", "lease accounting software", "TLE"],
    },
  },
};
